import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';
import supabase from '../../lib/supabase';
import { useOrgContext } from '../../context/OrgContext';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

type Rollup = {
  id?: string;
  org_id: string;
  period_start: string;
  period_end: string;
  messages_inbound: number | null;
  messages_outbound: number | null;
  contacts_created: number | null;
  leads_created: number | null;
  appointments_completed: number | null;
  first_response_avg_seconds: number | null;
};

const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const percentFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1, signDisplay: 'always' });

const formatSeconds = (seconds: number | null | undefined) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

const formatDateLabel = (iso: string) => {
  const date = new Date(`${iso}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const sumForKey = (rows: Rollup[], key: keyof Rollup) =>
  rows.reduce((acc, row) => acc + (Number(row[key] ?? 0)), 0);

const averageForKey = (rows: Rollup[], key: keyof Rollup) => {
  const values = rows
    .map((row) => (row[key] == null ? null : Number(row[key])))
    .filter((val): val is number => typeof val === 'number' && Number.isFinite(val));
  if (!values.length) return null;
  return Math.round(values.reduce((acc, val) => acc + val, 0) / values.length);
};

const percentDelta = (current: number, previous: number | null) => {
  if (previous == null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { orgId, loading: orgLoading } = useOrgContext();
  const [rollups, setRollups] = useState<Rollup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.classList.contains('dark'));

  useEffect(() => {
    if (!orgId) {
      if (!orgLoading) {
        setRollups([]);
        setLoading(false);
      }
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      const since = new Date();
      since.setUTCDate(since.getUTCDate() - 30);
      const { data, error } = await supabase
        .from('kpi_rollups')
        .select('*')
        .eq('org_id', orgId)
        .gte('period_start', since.toISOString().slice(0, 10))
        .order('period_start', { ascending: true });
      if (!active) return;
      if (error) {
        setError(error.message);
        setRollups([]);
      } else {
        setRollups((data as Rollup[]) ?? []);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [orgId, orgLoading]);

  const { currentWindow, comparisonWindow } = useMemo(() => {
    if (!rollups.length) return { currentWindow: [], comparisonWindow: [] };
    const lastSeven = rollups.slice(-7);
    const previousSeven = rollups.slice(-14, -7);
    if (lastSeven.length < 7) {
      return { currentWindow: rollups, comparisonWindow: previousSeven };
    }
    return { currentWindow: lastSeven, comparisonWindow: previousSeven };
  }, [rollups]);

  const summaryCards = useMemo(() => {
    if (!currentWindow.length) return [] as {
      key: string;
      label: string;
      value: string;
      deltaLabel: string;
      improveIsPositive?: boolean;
      highlight?: boolean;
    }[];

    const inboundCurrent = sumForKey(currentWindow, 'messages_inbound');
    const inboundPrev = comparisonWindow.length ? sumForKey(comparisonWindow, 'messages_inbound') : null;

    const contactsCurrent = sumForKey(currentWindow, 'contacts_created');
    const contactsPrev = comparisonWindow.length ? sumForKey(comparisonWindow, 'contacts_created') : null;

    const leadsCurrent = sumForKey(currentWindow, 'leads_created');
    const leadsPrev = comparisonWindow.length ? sumForKey(comparisonWindow, 'leads_created') : null;

    const apptsCurrent = sumForKey(currentWindow, 'appointments_completed');
    const apptsPrev = comparisonWindow.length ? sumForKey(comparisonWindow, 'appointments_completed') : null;

    const responseCurrent = averageForKey(currentWindow, 'first_response_avg_seconds');
    const responsePrev = comparisonWindow.length ? averageForKey(comparisonWindow, 'first_response_avg_seconds') : null;

    const buildDeltaLabel = (delta: number | null) => {
      if (delta == null) return t('crm.reports.cards.vsPrevious');
      return `${percentFormatter.format(delta)} ${t('crm.reports.cards.vsPrevious')}`;
    };

    const responseDeltaLabel = () => {
      if (responsePrev == null || responseCurrent == null) return t('crm.reports.cards.vsPrevious');
      const diff = responseCurrent - responsePrev;
      const sign = diff > 0 ? '+' : '';
      return `${sign}${diff}s ${t('crm.reports.cards.vsPrevious')}`;
    };

    return [
      {
        key: 'inboundMessages',
        label: 'Inbound messages',
        value: numberFormatter.format(inboundCurrent),
        deltaLabel: buildDeltaLabel(percentDelta(inboundCurrent, inboundPrev)),
      },
      {
        key: 'contactsCaptured',
        label: 'Contacts captured',
        value: numberFormatter.format(contactsCurrent),
        deltaLabel: buildDeltaLabel(percentDelta(contactsCurrent, contactsPrev)),
      },
      {
        key: 'leadsCreated',
        label: 'Leads created',
        value: numberFormatter.format(leadsCurrent),
        deltaLabel: buildDeltaLabel(percentDelta(leadsCurrent, leadsPrev)),
      },
      {
        key: 'appointmentsCompleted',
        label: 'Appointments completed',
        value: numberFormatter.format(apptsCurrent),
        deltaLabel: buildDeltaLabel(percentDelta(apptsCurrent, apptsPrev)),
      },
      {
        key: 'avgFirstResponse',
        label: 'Avg first response',
        value: formatSeconds(responseCurrent),
        deltaLabel: responseDeltaLabel(),
        improveIsPositive: false,
        highlight: true,
      },
    ];
  }, [currentWindow, comparisonWindow, t]);

  const chartDataMessaging = useMemo(() => {
    if (!rollups.length) return null;
    const labels = rollups.map((row) => formatDateLabel(row.period_start));
    return {
      labels,
      datasets: [
        {
          label: 'Inbound',
          data: rollups.map((row) => Number(row.messages_inbound ?? 0)),
          borderColor: 'rgba(14,165,233,0.9)',
          backgroundColor: 'rgba(14,165,233,0.2)',
          tension: 0.35,
          fill: true,
        },
        {
          label: 'Outbound',
          data: rollups.map((row) => Number(row.messages_outbound ?? 0)),
          borderColor: 'rgba(94,234,212,0.9)',
          backgroundColor: 'rgba(94,234,212,0.15)',
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [rollups]);

  const chartDataPipeline = useMemo(() => {
    if (!rollups.length) return null;
    const labels = rollups.map((row) => formatDateLabel(row.period_start));
    return {
      labels,
      datasets: [
        {
          label: 'Contacts',
          data: rollups.map((row) => Number(row.contacts_created ?? 0)),
          borderColor: 'rgba(59,130,246,0.9)',
          backgroundColor: 'rgba(59,130,246,0.15)',
          tension: 0.35,
          fill: true,
        },
        {
          label: 'Leads',
          data: rollups.map((row) => Number(row.leads_created ?? 0)),
          borderColor: 'rgba(147,197,253,0.9)',
          backgroundColor: 'rgba(147,197,253,0.12)',
          tension: 0.35,
          fill: true,
        },
        {
          label: 'Appointments',
          data: rollups.map((row) => Number(row.appointments_completed ?? 0)),
          borderColor: 'rgba(16,185,129,0.9)',
          backgroundColor: 'rgba(16,185,129,0.12)',
          tension: 0.35,
          fill: true,
        },
      ],
    };
  }, [rollups]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDarkMode ? '#d1d5db' : '#374151',
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
        titleColor: isDarkMode ? '#f3f4f6' : '#111827',
        bodyColor: isDarkMode ? '#d1d5db' : '#374151',
        borderColor: isDarkMode ? '#4b5563' : '#e5e7eb',
        borderWidth: 1,
        callbacks: {
          label: (ctx: any) => {
            const label = ctx.dataset.label || '';
            const value = ctx.formattedValue || ctx.raw;
            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: isDarkMode ? '#9ca3af' : '#6b7280',
        },
        grid: {
          color: isDarkMode ? '#374151' : '#e5e7eb',
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: isDarkMode ? '#9ca3af' : '#6b7280',
        },
        grid: {
          color: isDarkMode ? '#374151' : '#e5e7eb',
        },
      },
    },
  }), [isDarkMode]);

  const recentRows = useMemo(() => rollups.slice(-14).reverse(), [rollups]);

  // Calculate mobile scorecard metrics
  const mobileMetrics = useMemo(() => {
    if (!currentWindow.length) {
      return {
        dailyContacts: 0,
        dailyTarget: 150, // Default target
        conversionRate: 0,
        pipelineValue: null as number | null,
      };
    }

    const dailyContacts = sumForKey(currentWindow, 'contacts_created') / currentWindow.length;
    const dailyTarget = 150; // Can be made configurable later
    const conversionRate = currentWindow.length > 0 
      ? (sumForKey(currentWindow, 'appointments_completed') / Math.max(sumForKey(currentWindow, 'contacts_created'), 1)) * 100
      : 0;
    
    // Calculate pipeline value (estimated from leads created)
    const leadsCreated = sumForKey(currentWindow, 'leads_created');
    const pipelineValue = leadsCreated * 1000; // Assume avg €1k per lead, can be refined

    return {
      dailyContacts: Math.round(dailyContacts),
      dailyTarget,
      conversionRate: Number(conversionRate.toFixed(1)),
      pipelineValue,
    };
  }, [currentWindow]);

  const progressPercentage = useMemo(() => {
    return Math.min((mobileMetrics.dailyContacts / mobileMetrics.dailyTarget) * 100, 100);
  }, [mobileMetrics]);

  const getProgressColor = (percentage: number) => {
    if (percentage < 50) return 'bg-red-500';
    if (percentage < 90) return 'bg-yellow-500';
    return 'bg-emerald-500';
  };

  const getProgressBgColor = (percentage: number) => {
    if (percentage < 50) return 'bg-red-500/20';
    if (percentage < 90) return 'bg-yellow-500/20';
    return 'bg-emerald-500/20';
  };

  // Extract trend percentage from delta label for desktop cards
  const extractTrendPercentage = (deltaLabel: string): number | null => {
    const match = deltaLabel.match(/([+-]?\d+\.?\d*)%/);
    return match ? parseFloat(match[1]) : null;
  };

  return (
    <div className="space-y-4 lg:space-y-6 w-full max-w-full overflow-x-hidden">
      <h1 className="text-xl sm:text-2xl font-bold md:font-semibold mb-4 text-gray-900 dark:text-white">{t('crm.reports.title')}</h1>
      {error && <div className="mb-4 rounded-lg border-2 border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">{error}</div>}
      {loading ? (
        <div className="p-6 text-sm text-gray-500 dark:text-gray-400">{t('crm.reports.loading')}</div>
      ) : rollups.length === 0 ? (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 p-6 text-sm text-gray-500 dark:text-gray-400 rounded-xl">
          {t('crm.reports.empty')}
        </div>
      ) : (
        <>
          {/* Desktop: Summary Cards with Trend Indicators */}
          <div className="hidden md:grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {summaryCards.map((card) => {
              const trend = extractTrendPercentage(card.deltaLabel);
              const isPositive = trend ? (card.improveIsPositive === false ? trend < 0 : trend > 0) : null;
              
              return (
                <div
                  key={card.key}
                  className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md hover:shadow-lg transition-shadow ${card.highlight ? 'border-cyan-500 dark:border-cyan-400 ring-2 ring-cyan-500/20' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t(`crm.reports.cards.${card.key}`)}</div>
                    {trend !== null && (
                      <div className={`flex items-center gap-1 text-xs font-semibold ${isPositive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {isPositive ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        <span>{Math.abs(trend)}%</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">{card.value}</div>
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">{card.deltaLabel}</div>
                </div>
              );
            })}
          </div>

          {/* Mobile: Sales Scorecard */}
          <div className="block md:hidden space-y-4">
            {/* Daily Grind Progress */}
            <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Target className="w-5 h-5 text-cyan-500" />
                  Daily Grind
                </h2>
                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                  {mobileMetrics.dailyContacts} / {mobileMetrics.dailyTarget}
                </span>
              </div>
              
              {/* Linear Progress Bar */}
              <div className={`relative w-full h-6 rounded-full overflow-hidden ${getProgressBgColor(progressPercentage)}`}>
                <div
                  className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out ${getProgressColor(progressPercentage)}`}
                  style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-gray-900 dark:text-white drop-shadow-sm">
                    {progressPercentage.toFixed(0)}%
                  </span>
                </div>
              </div>
              
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400 text-center">
                {progressPercentage >= 100 
                  ? "🎉 Target achieved! Keep it up!" 
                  : progressPercentage >= 90
                  ? "Almost there! Push for 100%!"
                  : progressPercentage >= 50
                  ? "Good progress! Keep going!"
                  : "Let's get moving! You've got this!"}
              </p>
            </div>

            {/* Conversion Pulse */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-lg">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Conversion Rate
                </div>
                <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  {mobileMetrics.conversionRate}%
                </div>
                <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Success Rate
                </div>
              </div>
              
              {mobileMetrics.pipelineValue !== null && (
                <div className="bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-lg">
                  <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                    Pipeline Value
                  </div>
                  <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">
                    € {numberFormatter.format(mobileMetrics.pipelineValue)}
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Estimated
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Desktop: Charts (Hidden on Mobile) */}
          <div className="hidden md:grid mt-6 grid-cols-1 gap-6 lg:grid-cols-2">
            {chartDataMessaging && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
                <div className="mb-3 text-sm uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.reports.charts.messagingVolume')}</div>
                <div className="h-64">
                  <Line data={chartDataMessaging} options={chartOptions} />
                </div>
              </div>
            )}
            {chartDataPipeline && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-md">
                <div className="mb-3 text-sm uppercase tracking-[0.3em] text-gray-500 dark:text-gray-400">{t('crm.reports.charts.pipelineVelocity')}</div>
                <div className="h-64">
                  <Line data={chartDataPipeline} options={chartOptions} />
                </div>
              </div>
            )}
          </div>

          {/* Desktop: Data Table (Hidden on Mobile) */}
          <div className="hidden md:block mt-6 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-xs uppercase tracking-[0.2em] text-gray-700 dark:text-gray-300">
                <tr>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.date')}</th>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.inbound')}</th>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.outbound')}</th>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.contacts')}</th>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.leads')}</th>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.appointments')}</th>
                  <th className="px-4 py-3 font-semibold">{t('crm.reports.table.avgResponse')}</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.map((row) => (
                    <tr key={`${row.org_id}-${row.period_start}`} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-colors cursor-pointer">
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">{formatDateLabel(row.period_start)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{numberFormatter.format(Number(row.messages_inbound ?? 0))}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{numberFormatter.format(Number(row.messages_outbound ?? 0))}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{numberFormatter.format(Number(row.contacts_created ?? 0))}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{numberFormatter.format(Number(row.leads_created ?? 0))}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{numberFormatter.format(Number(row.appointments_completed ?? 0))}</td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatSeconds(row.first_response_avg_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsPage;



